import { useLazyQuery, useMutation } from '@apollo/client';
import { FC } from 'react';
import {
  CasesCredCaseFileUploadWithGeneratePdfPacketButtonContainerCredentialVerificationCaseV1UpdateDocument,
  CasesCredentialVerificationV1CredCaseFileUploadButtonWithPreviewContainerCredentialVerificationCaseV1Document,
  CasesCredentialVerificationV1CredCaseFileUploadButtonWithPreviewContainerCredentialVerificationCaseV1ImageFileCreateAuthHeaderDocument,
  CasesCredentialVerificationV1CredCaseFileUploadButtonWithPreviewContainerCredentialVerificationCaseV1PdfFileCreateAuthHeaderDocument,
  CredentialVerificationCaseV1UpdateInput,
  FileInfo
} from '../../../../../../generated';
import { GetCredCaseAuthHeader } from './cred-case-get-auth-header';
import { AuthResult, AzureBlobResponse } from '../../../../../shared/file-upload/azure-upload';
import { FileUploadWithGeneratePdfPacketButton } from './file-upload-with-generate-pdf-packet-button';

interface FileUploadWithGeneratePdfPacketButtonContainerProps {
  caseId: string;
  title: string;
  buttonText?: string;
  description?: string;
  permittedContentTypes?: string[];
  permittedExtensions?: string[];
  isPreviewOnly?: boolean;
  setIsEditing?: React.Dispatch<React.SetStateAction<boolean>>;
  verificationFileName: string;
  isShowFileName?: boolean;
  verificationFileNames: Map<string, string>;
  isVerificationPacket: boolean;
  isUploadPreviewImage?: boolean;
}

export const FileUploadWithGeneratePdfPacketButtonContainer: FC<FileUploadWithGeneratePdfPacketButtonContainerProps> = ({
  isPreviewOnly = false,
  ...props
}) => {
  const [getAuthHeaderForPdfFile] = useMutation(
    CasesCredentialVerificationV1CredCaseFileUploadButtonWithPreviewContainerCredentialVerificationCaseV1PdfFileCreateAuthHeaderDocument
  );

  const [getAuthHeaderForImageFile] = useMutation(
    CasesCredentialVerificationV1CredCaseFileUploadButtonWithPreviewContainerCredentialVerificationCaseV1ImageFileCreateAuthHeaderDocument
  );

  const [getFilePreviewData] = useLazyQuery(CasesCredentialVerificationV1CredCaseFileUploadButtonWithPreviewContainerCredentialVerificationCaseV1Document, {
    fetchPolicy: 'network-only'
  });

  const [updatedCredVerificationCaseV1] = useMutation(CasesCredCaseFileUploadWithGeneratePdfPacketButtonContainerCredentialVerificationCaseV1UpdateDocument);

  const handleUpdateAfterUploadSuccess = async (uploadedData: AzureBlobResponse) => {
    const assetPath = 'private';
    const fieldName = props.isVerificationPacket ? 'verificationPacket' : 'verificationComponentPacket';

    const input: CredentialVerificationCaseV1UpdateInput = {
      id: props.caseId,
      assets: {
        [assetPath]: {
          [fieldName]: uploadedData.blobUrl,
          [`${fieldName}Version`]: uploadedData.versionId
        }
      }
    };
    await updatedCredVerificationCaseV1({
      variables: {
        input
      }
    });
  };

  const handleAuthorizeRequest = async (fileData: File, filename: string): Promise<AuthResult> => {
    return GetCredCaseAuthHeader(props.caseId, fileData, filename, getAuthHeaderForPdfFile, getAuthHeaderForImageFile);
  };

  const handleFetchFilePreviewData = async (filename: string): Promise<FileInfo> => {
    const previewData = await getFilePreviewData({
      variables: {
        credentialVerificationCaseV1Id: props.caseId,
        fileName: filename
      }
    });
    return previewData?.data?.credentialVerificationCaseV1?.fileByFileName as FileInfo;
  };

  return (
    <FileUploadWithGeneratePdfPacketButton
      caseId={props.caseId}
      title={props.title}
      buttonText={props.buttonText}
      description={props.description}
      permittedContentTypes={props.permittedContentTypes}
      permittedExtensions={props.permittedExtensions}
      isPreviewOnly={isPreviewOnly}
      setIsEditing={props.setIsEditing}
      onAuthorizeRequest={(fileData: File) => handleAuthorizeRequest(fileData, props.verificationFileName)}
      fetchFilePreviewData={() => handleFetchFilePreviewData(props.verificationFileName)}
      onUploadSuccess={(response: AzureBlobResponse) => handleUpdateAfterUploadSuccess(response)}
      isShowFileName={props.isShowFileName}
      isUploadingPreviewImage={false}
      isVerificationPacket={props.isVerificationPacket}
      verificationFileNames={props.verificationFileNames}
      verificationFileName={props.verificationFileName}
    />
  );
};
